import { RemovableCard, Text } from '@dixa/lib-shared-habla';
import { useEffectOnce } from 'react-use';

import { useHasRequiredAttributes } from 'utils/customFields';

import {
  BulkModalActionBaseProps,
  BulkModalCloseAction,
} from '../../../types/conversationsBulkModal.types';
import { conversationsBulkModalActionLabel } from '../../../utils/conversationsBulkModalActionLabel';

type ConversationsBulkModalCloseActionProps =
  BulkModalActionBaseProps<BulkModalCloseAction>;

/** Represents the component for the conversations bulk modal close action. */
export const ConversationsBulkModalCloseAction = ({
  conversationIds,
  onChange,
  onRemove,
}: ConversationsBulkModalCloseActionProps): JSX.Element => {
  const hasRequiredAttributes = useHasRequiredAttributes({
    entityId: 'conversation',
  });

  useEffectOnce(() => {
    onChange({
      type: 'close',
      isValid: true,
      arg: { csids: conversationIds },
    });
  });

  return (
    <RemovableCard
      icon="close"
      title={conversationsBulkModalActionLabel['close']}
      onRemove={() => onRemove('close')}
    >
      <Text.Paragraph subdued withMarginBottom>
        Please note that closing conversations in bulk will have impacts as
        described below.
      </Text.Paragraph>

      <ul>
        <li>
          Conversations without assigned agents will be assigned to the user
          performing the bulk action i.e. you.
        </li>

        <li>
          Conversations with CSAT enabled will trigger satisfaction surveys to
          be sent to the customers. You can do a bulk action to cancel CSAT
          first if you wish not to send any surveys.
        </li>

        <li>Conversations will be closed without wrap-up time for agents.</li>

        {hasRequiredAttributes && (
          <li>
            Conversations missing required attributes will still be closed.
            Consequently, some conversations might appear with empty required
            attributes in your reporting.
          </li>
        )}
      </ul>
    </RemovableCard>
  );
};
